import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { DealViews } from "constants/deal";
import { usePrompt, useUrlHooks } from "hooks";
import { useDealQueriesSelect, useReportsQueriesSelector, useUserActions } from "store";
import { IBookmarkType, IReportType } from "store/types";

import { buildBookmarkSaveParams } from "./bookmark-page";
import { PeoplePicker } from "./PeoplePicker";
import { CurrentPageContext, ShareScope } from "./types";

interface Props {
  currentPage: CurrentPageContext | null;
  currentPathname: string;
  onSubmit: (result: boolean) => void;
  onCancel: () => void;
}

const MAX_NAME_LENGTH = 60;

export const SaveViewPrompt: React.FC<Props> = ({ currentPage, currentPathname, onSubmit, onCancel }) => {
  const { createBookmarkAction } = useUserActions();
  const { queryParams } = useUrlHooks();
  const reportsQueries = useReportsQueriesSelector();
  const { view } = useDealQueriesSelect();
  const { search } = useLocation();
  const openPrompt = usePrompt();

  const [name, setName] = useState("");
  const [share, setShare] = useState<ShareScope>("private");
  const [specificIds, setSpecificIds] = useState<number[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showNameError, setShowNameError] = useState(false);
  const [showSharedError, setShowSharedError] = useState(false);
  const [apiError, setApiError] = useState(false);

  const handleChoosePeople = async (anchor: HTMLElement | null) => {
    if (!anchor) return;
    const result = await openPrompt({
      Component: PeoplePicker,
      componentType: "popover",
      anchorEl: anchor,
      anchorOrigin: { vertical: "bottom", horizontal: "left" },
      transformOrigin: { vertical: "top", horizontal: "left" },
      props: { initialSelectedIds: specificIds }
    });
    if (result) {
      setSpecificIds(result as number[]);
      if (showSharedError) setShowSharedError(false);
    }
  };

  const isInvestorViewDashboard = currentPage?.type === "deal" && view === DealViews.Dashboard;

  const handleClose = () => {
    if (submitting) return;
    onCancel();
  };

  const handleSubmit = async () => {
    if (!currentPage) return;
    if (!name.trim()) {
      setShowNameError(true);
      return;
    }
    if (share === "shared" && specificIds.length === 0) {
      setShowSharedError(true);
      return;
    }
    setSubmitting(true);
    setApiError(false);
    try {
      const sharePayload =
        share === "shared" ? { visibility: "shared" as const, shared_with: specificIds } : { visibility: share };

      const built = buildBookmarkSaveParams(currentPage, {
        pathname: currentPathname,
        search: search || "",
        queryParams,
        reportsQueries
      });
      if (!built) {
        setApiError(true);
        setSubmitting(false);
        return;
      }

      const resolvedType: IBookmarkType = isInvestorViewDashboard ? "investor_view" : currentPage.type;
      // createBookmarkAction's report_type does not accept "external" (unlike updateBookmarkAction).
      const reportTypeForCreate =
        built.reportType && built.reportType !== IReportType.EXTERNAL ? built.reportType : undefined;
      await createBookmarkAction({
        name: name.trim(),
        params: built.params,
        type: resolvedType,
        report_type: reportTypeForCreate,
        is_default: isDefault,
        ...sharePayload
      });
      toast.success("View saved");
      onSubmit(true);
    } catch {
      setApiError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      gap="20px"
      sx={{
        width: "440px",
        maxWidth: "calc(100vw - 40px)",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow:
          "0px 2px 4px -1px rgba(0,0,0,.20), 0px 4px 5px 0px rgba(0,0,0,.14), 0px 1px 10px 0px rgba(0,0,0,.12)",
        padding: "32px",
        outline: "none"
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap="12px"
        sx={{ borderBottom: "1px solid #dbe8f5", paddingBottom: "16px" }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500, color: "#071f35", lineHeight: "24px" }}>
            Save view
          </Typography>
          {currentPage && (
            <Typography sx={{ fontSize: "14px", color: "#5e7a95", lineHeight: "20px", marginTop: "4px" }}>
              Save the current filters, columns, and sorting on {currentPage.pageLabel} so you can return to them later.
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={submitting}
          sx={{ color: "#5e7a95", padding: "4px", "&:hover": { backgroundColor: "#ecf1f8" } }}
        >
          <CloseIcon sx={{ fontSize: "16px" }} />
        </IconButton>
      </Stack>

      <Stack gap="16px">
        <Stack gap="6px">
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071f35" }}>
            View name<span style={{ color: "#d32f2f", marginLeft: "2px" }}>*</span>
          </Typography>
          <TextField
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (showNameError) setShowNameError(false);
            }}
            placeholder="e.g. Q4 underperformers"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={showNameError}
            size="small"
            fullWidth
          />
          {showNameError && (
            <Stack direction="row" alignItems="center" gap="4px">
              <ErrorOutlineIcon sx={{ fontSize: "12px", color: "#d32f2f" }} />
              <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>View name is required.</Typography>
            </Stack>
          )}
        </Stack>

        <Stack gap="6px">
          <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071f35" }}>Share</Typography>
          <Box sx={{ border: "1px solid #dbe8f5", borderRadius: "8px", overflow: "hidden" }}>
            <ShareRadio
              icon={<LockOutlinedIcon sx={{ fontSize: "16px" }} />}
              title="Only me"
              help="Private to you."
              value="private"
              selected={share === "private"}
              onSelect={() => {
                setShare("private");
                if (showSharedError) setShowSharedError(false);
              }}
            />
            <ShareRadio
              icon={<PeopleAltOutlinedIcon sx={{ fontSize: "16px" }} />}
              title="Specific people"
              help={
                share === "shared" && specificIds.length > 0
                  ? `${specificIds.length} ${specificIds.length === 1 ? "person" : "people"} selected.`
                  : "Choose teammates who can open this view."
              }
              value="shared"
              selected={share === "shared"}
              onSelect={() => setShare("shared")}
              action={
                share === "shared" ? (
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleChoosePeople(e.currentTarget);
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "primary.main",
                      padding: 0,
                      minWidth: 0,
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                      marginTop: "4px",
                      alignSelf: "flex-start",
                      "&:hover": { color: "primary.dark", backgroundColor: "transparent" }
                    }}
                  >
                    {specificIds.length > 0 ? "Change people…" : "Choose people…"}
                  </Button>
                ) : null
              }
            />
            <ShareRadio
              icon={<PublicOutlinedIcon sx={{ fontSize: "16px" }} />}
              title="Everyone on my team"
              help="Anyone in your workspace can open this view."
              value="account"
              selected={share === "account"}
              onSelect={() => {
                setShare("account");
                if (showSharedError) setShowSharedError(false);
              }}
              isLast
            />
          </Box>
          {showSharedError && (
            <Stack direction="row" alignItems="center" gap="4px">
              <ErrorOutlineIcon sx={{ fontSize: "12px", color: "#d32f2f" }} />
              <Typography sx={{ fontSize: "12px", color: "#d32f2f" }}>
                Choose at least one person to share with.
              </Typography>
            </Stack>
          )}
        </Stack>

        <FormControlLabel
          control={<Switch checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} color="primary" />}
          label={
            <Stack sx={{ ml: "4px" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "text.primary", lineHeight: "20px" }}>
                Set as default view
              </Typography>
              {currentPage && (
                <Typography sx={{ fontSize: "12px", color: "text.secondary", lineHeight: 1.4, marginTop: "2px" }}>
                  Open this view automatically when you visit {currentPage.pageLabel}.
                </Typography>
              )}
            </Stack>
          }
          sx={{ alignItems: "flex-start", ml: 0, "& .MuiFormControlLabel-label": { paddingTop: "6px" } }}
        />

        {apiError && (
          <Stack
            direction="row"
            alignItems="flex-start"
            gap="8px"
            sx={{
              backgroundColor: "#fcf8f8",
              color: "#d32f2f",
              padding: "12px",
              borderRadius: "4px"
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: "14px", marginTop: "1px" }} />
            <Typography sx={{ fontSize: "13px", lineHeight: 1.4 }}>
              View couldn't be saved. Check your connection and try again.
            </Typography>
          </Stack>
        )}
      </Stack>

      <Stack direction="row" justifyContent="flex-end" gap="8px" sx={{ paddingTop: "8px" }}>
        <Button onClick={handleClose} disabled={submitting} variant="text" sx={pillBtnSx}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={submitting || !name.trim()}
          variant="contained"
          sx={pillBtnSx}
          startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : undefined}
        >
          {submitting ? "Saving…" : "Save view"}
        </Button>
      </Stack>
    </Stack>
  );
};

interface ShareRadioProps {
  icon: React.ReactNode;
  title: string;
  help: string;
  value: ShareScope;
  selected: boolean;
  onSelect: () => void;
  isLast?: boolean;
  action?: React.ReactNode;
}

const ShareRadio: React.FC<ShareRadioProps> = ({ icon, title, help, selected, onSelect, isLast, action }) => (
  <Stack
    component="label"
    direction="row"
    alignItems="flex-start"
    gap="12px"
    onClick={onSelect}
    sx={{
      padding: "12px",
      cursor: "pointer",
      borderBottom: isLast ? "none" : "1px solid #dbe8f5",
      transition: "background 120ms",
      "&:hover": { backgroundColor: "#ecf1f8" }
    }}
  >
    <Radio checked={selected} sx={{ padding: 0, marginTop: "2px" }} />
    <Stack sx={{ color: "#5e7a95", marginTop: "1px" }}>{icon}</Stack>
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#071f35", lineHeight: "20px" }}>{title}</Typography>
      <Typography sx={{ fontSize: "12px", color: "#5e7a95", lineHeight: 1.4, marginTop: "2px" }}>{help}</Typography>
      {action}
    </Stack>
  </Stack>
);

const pillBtnSx = {
  textTransform: "none",
  fontSize: "14px",
  fontWeight: 500,
  height: "36px",
  padding: "0 16px",
  borderRadius: "128px"
};
